// Add/Edit trip. Full-replace payload; destination-local wall times with
// explicit DST fold choices; all-day uses an inclusive last-day input that is
// sent as an exclusive next-midnight end (labelled). Dirty/busy leave guards.
import { useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { Modal } from "../../../components/Modal.jsx";
import { PURPOSES, addDays, validCivilDate } from "../../../lib/travelTime.js";
import { createTrip, updateTrip } from "../../../data/projectTravelClient.js";
import { registerNavigationGuard } from "../recordNavigationGuard.js";
import { tx } from "./travelStyles.js";
import { TravelOfficePicker } from "./TravelOfficePicker.jsx";
import { draftFromTrip, validateDraft, buildTripPayload, wallTimeStatus, countryChoices, EMPTY_DRAFT } from "./travelModel.js";

function Field({ label, error, children, htmlFor }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label htmlFor={htmlFor} style={tx.label}>{label}</label>
      {children}
      {error && <div role="alert" style={tx.err}>{error}</div>}
    </div>
  );
}

function Occurrence({ local, zone, value, onChange, testId }) {
  if (wallTimeStatus(local, zone) !== "fold") return null;
  return (
    <div style={{ marginTop: 4, fontSize: T.fsXs, color: T.travelAmberFg }}>
      This time happens twice (clock change).{" "}
      <select data-testid={testId} aria-label="Which occurrence" value={value || ""} onChange={(e) => onChange(e.target.value || null)} style={{ ...tx.input, width: "auto", padding: "2px 6px", fontSize: T.fsXs }}>
        <option value="">Choose…</option><option value="earlier">Earlier (before change)</option><option value="later">Later (after change)</option>
      </select>
    </div>
  );
}

export function TripEditorModal({ open, trip, options, defaultProjectId = null, onClose, onSaved }) {
  const isEdit = Boolean(trip?.id);
  const initial = useMemo(() => {
    const d = isEdit ? draftFromTrip(trip) : { ...EMPTY_DRAFT, travelers: [], projectId: defaultProjectId || "" };
    return d;
  }, [trip, isEdit, defaultProjectId]);
  const [draft, setDraft] = useState(initial);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [ask, setAsk] = useState(null);
  const keepEditingRef = useRef(null);
  const guardTriggerRef = useRef(null);
  const [addPerson, setAddPerson] = useState("");
  useEffect(() => { if (open) { setDraft(initial); setTouched(false); setServerError(null); setAsk(null); setBusy(false); } }, [open, initial]);

  const dirty = open && JSON.stringify(draft) !== JSON.stringify(initial);
  const state = useRef({ dirty, busy });
  state.current = { dirty, busy };
  const showLeaveGuard = (proceed) => {
    guardTriggerRef.current = document.activeElement;
    setAsk({proceed});
  };
  useEffect(() => { if (ask) keepEditingRef.current?.focus({preventScroll:true}); }, [ask]);
  useEffect(() => {
    if (!open) return undefined;
    const off = registerNavigationGuard({
      isBlocking: () => state.current.dirty || state.current.busy,
      onBlocked: (proceed) => { if (!state.current.busy) showLeaveGuard(proceed); },
    });
    const onBefore = (e) => { if (state.current.dirty || state.current.busy) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBefore);
    return () => { off?.(); window.removeEventListener("beforeunload", onBefore); };
  }, [open]);

  const requestClose = () => {
    if (busy) return;
    if (dirty) showLeaveGuard(onClose); else onClose();
  };

  const set = (patch) => setDraft((d) => ({ ...d, ...patch,
    ...(patch.destinationTz !== undefined && patch.destinationTz !== d.destinationTz ? {
      startOccurrence:null,endOccurrence:null,
      travelers:d.travelers.map(t=>({...t,startOccurrence:null,endOccurrence:null})),
    } : {}),
  }));
  const errors = touched ? validateDraft(draft) : {};
  const projects = options?.projects || [];
  const milestones = (options?.milestones || []).filter((m) => m.projectId === draft.projectId);
  const locations = options?.locations || [];
  const markets = options?.markets || [];
  const people = options?.people || [];
  const countries = countryChoices(options);
  const zones = [...new Set([...markets, ...locations].map((r) => r.timeZone).filter(Boolean))].sort();
  const currentProjectMissing = draft.projectId && !projects.some((p) => p.id === draft.projectId);

  const pickLocation = (id) => {
    const loc = locations.find((l) => l.id === id);
    set({ destinationLocationId: id, destinationMarketId: loc?.marketId || "", destinationLabel: loc?.label || "", destinationCountry: loc?.country || "", destinationTz: loc?.timeZone || "" });
  };
  const pickMarket = (id) => {
    const m = markets.find((x) => x.id === id);
    set({ destinationMarketId: id, destinationLocationId: "", destinationLabel: draft.destinationLabel || m?.name || "", destinationCountry: m?.country || draft.destinationCountry, destinationTz: m?.timeZone || "" });
  };
  const setMode = (mode) => set({ destMode: mode, destinationLocationId: "", destinationMarketId: mode === "free" ? "" : draft.destinationMarketId, ...(mode === "office" ? { destinationLabel: "", destinationCountry: "", destinationTz: "" } : {}) });
  const setTraveler = (i, patch) => setDraft((d) => ({ ...d, travelers: d.travelers.map((t, j) => j === i ? { ...t, ...patch } : t) }));

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(validateDraft(draft)).length) return;
    setBusy(true); setServerError(null);
    const body = buildTripPayload(draft);
    Promise.resolve().then(() => isEdit ? updateTrip(trip.id, body) : createTrip(body))
      .then((saved) => { setBusy(false); setDraft(initial); onSaved(saved); })
      .catch((err) => { setBusy(false); setServerError(err?.message || "Could not save this trip."); });
  };

  const tz = draft.destinationTz;
  const lastValid = validCivilDate(draft.lastDate);

  return (
    <Modal isOpen={open} onClose={requestClose} maxWidth={680} title={isEdit ? "Edit trip" : "Add trip"} ariaLabelledBy="travel-editor-title" testId="travel-editor" closeTestId="travel-editor-close" disableBackdropClose>
      <form onSubmit={submit} noValidate style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div style={{ padding: "14px 18px", overflowY: "auto", display: "grid", gap: 12 }}>
          {ask && (
            <div role="alert" data-testid="travel-editor-unsaved" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "8px 12px", borderRadius: 8, background: T.utilAmber, fontSize: T.fsSm }}>
              <span style={{ flex: 1 }}>This trip has unsaved changes. Leaving now discards them.</span>
               <button type="button" ref={keepEditingRef} style={tx.ghost} onClick={() => {
                 setAsk(null);
                 if (guardTriggerRef.current?.isConnected) guardTriggerRef.current.focus({preventScroll:true});
               }} data-testid="travel-editor-keep">Keep editing</button>
              <button type="button" style={tx.danger} onClick={() => { const p = ask.proceed; setAsk(null); setDraft(initial); p?.(); }} data-testid="travel-editor-discard">Discard</button>
            </div>
          )}
          {serverError && <div role="alert" data-testid="travel-editor-error" style={{ background: T.travelRedBg, border: `1px solid ${T.travelRedBorder}`, borderRadius: 8, padding: "8px 10px", fontSize: T.fsSm, color: T.travelRedFg }}>{serverError}</div>}

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)", gap: 12 }}>
            <Field label="Title" error={errors.title} htmlFor="tr-title"><input id="tr-title" data-testid="travel-input-title" style={tx.input} value={draft.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} /></Field>
            <Field label="Purpose" htmlFor="tr-purpose">
              <select id="tr-purpose" data-testid="travel-input-purpose" style={tx.input} value={draft.purposeType} onChange={(e) => set({ purposeType: e.target.value })}>
                {Object.entries(PURPOSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12 }}>
            <Field label="Project (optional)" htmlFor="tr-project">
              <select id="tr-project" data-testid="travel-input-project" style={tx.input} value={draft.projectId} onChange={(e) => set({ projectId: e.target.value, milestoneId: "" })}>
                <option value="">Not linked to a project</option>
                {currentProjectMissing && <option value={draft.projectId}>{trip?.projectName || "Current project"}</option>}
                {projects.map((p) => <option key={p.id} value={p.id} disabled={!p.canWrite}>{p.name}{p.canWrite ? "" : " (no edit access)"}</option>)}
              </select>
            </Field>
            <Field label="Milestone (optional)" error={errors.milestoneId} htmlFor="tr-milestone">
              <select id="tr-milestone" data-testid="travel-input-milestone" style={tx.input} value={draft.milestoneId} disabled={!draft.projectId} onChange={(e) => set({ milestoneId: e.target.value })}>
                <option value="">{draft.projectId ? "No milestone" : "Pick a project first"}</option>
                {draft.milestoneId && !milestones.some((m) => m.id === draft.milestoneId) && <option value={draft.milestoneId}>{trip?.milestoneName || "Current milestone"}</option>}
                {milestones.map((m) => <option key={m.id} value={m.id}>{m.name}{m.dueDate ? ` · due ${m.dueDate}` : ""}</option>)}
              </select>
            </Field>
          </div>

          <fieldset style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", margin: 0, display: "grid", gap: 10 }}>
            <legend style={{ ...tx.eyebrow, padding: "0 4px" }}>Destination</legend>
            <div role="radiogroup" aria-label="Destination type" style={{ display: "flex", gap: 14, fontSize: T.fsSm }}>
              {[["office", "Office"], ["market", "Market"], ["free", "Free text"]].map(([k, l]) => (
                <label key={k} style={{ display: "inline-flex", gap: 5, alignItems: "center", cursor: "pointer" }}>
                  <input type="radio" name="tr-dest" data-testid={`travel-dest-${k}`} checked={draft.destMode === k} onChange={() => setMode(k)} />{l}
                </label>
              ))}
            </div>
            {draft.destMode === "office" && (
              <Field label="Office" error={errors.destination} htmlFor="tr-office">
                <TravelOfficePicker locations={locations} selectedId={draft.destinationLocationId}
                  snapshotLabel={draft.destinationLabel} onSelect={pickLocation} resetKey={`${open}|${trip?.id || ""}`} />
              </Field>
            )}
            {draft.destMode === "market" && (
              <Field label="Market" error={errors.destination} htmlFor="tr-market">
                <select id="tr-market" data-testid="travel-input-market" style={tx.input} value={draft.destinationMarketId} onChange={(e) => pickMarket(e.target.value)}>
                  <option value="">Choose a market…</option>
                  {markets.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </Field>
            )}
            {draft.destMode !== "office" && (
              <Field label="Destination label" error={errors.destinationLabel} htmlFor="tr-label"><input id="tr-label" data-testid="travel-input-label" style={tx.input} maxLength={240} value={draft.destinationLabel} onChange={(e) => set({ destinationLabel: e.target.value })} /></Field>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12 }}>
              <Field label="Country" error={errors.destinationCountry} htmlFor="tr-country">
                <input id="tr-country" list="tr-country-list" data-testid="travel-input-country" style={tx.input}
                  value={draft.destinationCountry} maxLength={120} placeholder="Enter the destination country explicitly"
                  disabled={draft.destMode !== "free"} onChange={(e) => set({ destinationCountry: e.target.value })} />
                <datalist id="tr-country-list">{countries.map((c) => <option key={c} value={c} />)}</datalist>
              </Field>
              <Field label="Time zone (IANA)" error={errors.destinationTz} htmlFor="tr-tz">
                <input id="tr-tz" data-testid="travel-input-tz" list="tr-tz-list" style={tx.input} placeholder="e.g. Europe/London" value={draft.destinationTz} disabled={draft.destMode !== "free"} onChange={(e) => set({ destinationTz: e.target.value.trim() })} />
                <datalist id="tr-tz-list">{zones.map((z) => <option key={z} value={z} />)}</datalist>
              </Field>
            </div>
            {draft.destMode === "market" && draft.destinationMarketId && (!draft.destinationTz || !draft.destinationCountry) && <div role="alert" data-testid="travel-market-geo-missing" style={tx.err}>This market has no recorded {draft.destinationTz ? "country" : "time zone"}. Use Free text with explicit values.</div>}
            {draft.destMode === "office" && draft.destinationLocationId && !draft.destinationTz && <div role="alert" style={tx.err}>This office has no recorded time zone. Use Market or Free text with an explicit zone.</div>}
          </fieldset>

          <fieldset style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", margin: 0, display: "grid", gap: 10 }}>
            <legend style={{ ...tx.eyebrow, padding: "0 4px" }}>When · destination local time{tz ? ` (${tz})` : ""}</legend>
            <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: T.fsSm }}>
              <input type="checkbox" data-testid="travel-input-allday" checked={draft.allDay} onChange={(e) => set({ allDay: e.target.checked, startDate: draft.startDate || draft.startsLocal.slice(0, 10), lastDate: draft.lastDate || draft.endsLocal.slice(0, 10) })} /> All day
            </label>
            {draft.allDay ? (
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12 }}>
                <Field label="First day" error={errors.start} htmlFor="tr-sd">
                  <input id="tr-sd" type="date" data-testid="travel-input-start-date" style={tx.input} value={draft.startDate} onChange={(e) => set({ startDate: e.target.value,startOccurrence:null })} />
                  <Occurrence local={draft.startDate ? `${draft.startDate}T00:00` : ""} zone={tz} value={draft.startOccurrence} onChange={(v)=>set({startOccurrence:v})} testId="travel-start-occurrence" />
                </Field>
                <Field label="Last day (inclusive)" error={errors.end} htmlFor="tr-ld">
                  <input id="tr-ld" type="date" data-testid="travel-input-last-date" style={tx.input} value={draft.lastDate} onChange={(e) => set({ lastDate: e.target.value,endOccurrence:null })} />
                  {lastValid && <div data-testid="travel-exclusive-end" style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}>Saved end (exclusive): {addDays(draft.lastDate, 1)} 00:00</div>}
                  {lastValid && <Occurrence local={`${addDays(draft.lastDate,1)}T00:00`} zone={tz} value={draft.endOccurrence} onChange={(v)=>set({endOccurrence:v})} testId="travel-end-occurrence" />}
                </Field>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12 }}>
                <Field label="Starts" error={errors.start} htmlFor="tr-s">
                  <input id="tr-s" type="datetime-local" data-testid="travel-input-start" style={tx.input} value={draft.startsLocal} onChange={(e) => set({ startsLocal: e.target.value, startOccurrence: null })} />
                  <Occurrence local={draft.startsLocal} zone={tz} value={draft.startOccurrence} onChange={(v) => set({ startOccurrence: v })} testId="travel-start-occurrence" />
                </Field>
                <Field label="Ends" error={errors.end} htmlFor="tr-e">
                  <input id="tr-e" type="datetime-local" data-testid="travel-input-end" style={tx.input} value={draft.endsLocal} onChange={(e) => set({ endsLocal: e.target.value, endOccurrence: null })} />
                  <Occurrence local={draft.endsLocal} zone={tz} value={draft.endOccurrence} onChange={(v) => set({ endOccurrence: v })} testId="travel-end-occurrence" />
                </Field>
              </div>
            )}
          </fieldset>

          <fieldset style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", margin: 0, display: "grid", gap: 8 }}>
            <legend style={{ ...tx.eyebrow, padding: "0 4px" }}>Travelers</legend>
            {draft.travelers.length === 0 && <div style={{ fontSize: T.fsSm, color: T.mid }}>No travelers yet. A trip with no one on it does not cover a milestone.</div>}
            {errors.travelers && <div role="alert" style={tx.err}>{errors.travelers}</div>}
            {draft.travelers.map((t, i) => (
              <div key={t.personId} data-testid={`travel-traveler-${t.personId}`} style={{ borderTop: i ? `1px solid ${T.travelHair}` : 0, paddingTop: i ? 8 : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: T.fsSm }}>
                  <b>{t.displayName}</b>{t.org && <span style={{ color: T.mid }}>{t.org}</span>}
                  {!t.active && <span style={tx.eyebrow}>inactive · kept as recorded</span>}
                  <span style={{ flex: 1 }} />
                  <label style={{ display: "inline-flex", gap: 4, alignItems: "center", fontSize: T.fsXs }}>
                    <input type="checkbox" data-testid={`travel-override-${t.personId}`} checked={t.override} onChange={(e) => setTraveler(i, { override: e.target.checked, startsLocal: t.startsLocal || draft.startsLocal || (draft.startDate ? `${draft.startDate}T00:00` : ""), endsLocal: t.endsLocal || draft.endsLocal })} /> Own dates
                  </label>
                  <button type="button" style={{ ...tx.link, color: T.red, fontSize: T.fsXs }} data-testid={`travel-remove-${t.personId}`} onClick={() => setDraft((d) => ({ ...d, travelers: d.travelers.filter((_, j) => j !== i) }))}>Remove</button>
                </div>
                {t.override && (
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 10, marginTop: 6 }}>
                    <Field label="From (destination local)" error={errors[`traveler${i}Start`]}>
                      <input type="datetime-local" aria-label={`${t.displayName} from`} style={tx.input} value={t.startsLocal} onChange={(e) => setTraveler(i, { startsLocal: e.target.value, startOccurrence: null })} />
                      <Occurrence local={t.startsLocal} zone={tz} value={t.startOccurrence} onChange={(v) => setTraveler(i, { startOccurrence: v })} testId={`travel-ov-start-occ-${t.personId}`} />
                    </Field>
                    <Field label="Until (exclusive)" error={errors[`traveler${i}End`]}>
                      <input type="datetime-local" aria-label={`${t.displayName} until`} style={tx.input} value={t.endsLocal} onChange={(e) => setTraveler(i, { endsLocal: e.target.value, endOccurrence: null })} />
                      <Occurrence local={t.endsLocal} zone={tz} value={t.endOccurrence} onChange={(v) => setTraveler(i, { endOccurrence: v })} testId={`travel-ov-end-occ-${t.personId}`} />
                    </Field>
                  </div>
                )}
              </div>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <select aria-label="Add traveler" data-testid="travel-add-person" style={{ ...tx.input, flex: 1 }} value={addPerson} onChange={(e) => setAddPerson(e.target.value)}>
                <option value="">Add a person…</option>
                {people.filter((p) => !draft.travelers.some((t) => t.personId === p.personId)).map((p) => <option key={p.personId} value={p.personId}>{p.displayName}{p.org ? ` · ${p.org}` : ""}</option>)}
              </select>
              <button type="button" style={tx.ghost} disabled={!addPerson} data-testid="travel-add-person-btn" onClick={() => {
                const p = people.find((x) => x.personId === addPerson);
                if (!p) return;
                setDraft((d) => ({ ...d, travelers: [...d.travelers, { personId: p.personId, displayName: p.displayName, org: p.org, active: true, existing: false, override: false, startsLocal: "", endsLocal: "", startOccurrence: null, endOccurrence: null }] }));
                setAddPerson("");
              }}>Add</button>
            </div>
          </fieldset>

          <Field label="Notes" htmlFor="tr-notes"><textarea id="tr-notes" data-testid="travel-input-notes" rows={3} maxLength={4000} style={{ ...tx.input, resize: "vertical" }} value={draft.notes} onChange={(e) => set({ notes: e.target.value })} /></Field>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 18px", borderTop: `1px solid ${T.border}` }}>
          <button type="button" style={tx.ghost} disabled={busy} onClick={requestClose} data-testid="travel-editor-cancel">Cancel</button>
          <button type="submit" style={{ ...tx.primary, opacity: busy ? 0.6 : 1 }} disabled={busy} data-testid="travel-editor-save">{busy ? "Saving…" : isEdit ? "Save trip" : "Add trip"}</button>
        </div>
      </form>
    </Modal>
  );
}
